import { useQuery } from '@tanstack/react-query';
import {
  Building2Icon,
  ChevronRightIcon,
  GlobeIcon,
  LockIcon,
  MessageSquareIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import { type JSX, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { WorkspaceMemberPicker } from '@/features/workspace/WorkspaceMemberPicker.js';
import {
  offersSharingControls,
  type WorkspaceSharing,
} from '@/features/workspace/workspace-access.js';
import {
  artifactShareUrl,
  artifactSharesQueryOptions,
  useCreateArtifactShare,
  type ArtifactShareAccessMode,
} from '@/lib/api/artifact-shares.js';
import { groupMemberCountLabel } from '@/lib/api/directory-groups.js';
import {
  fetchArtifactAccess,
  fetchWorkspaceArtifact,
  useSetArtifactVisibility,
  type ArtifactVisibility,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';
import { copyToClipboard } from '@/lib/clipboard.js';
import { notify } from '@/lib/notify.js';
import { safeHttpUrl } from '@/lib/safe-url.js';
import { outsideTabLabel } from '@/lib/share-reach.js';
import { titleWhenTruncated } from '@/lib/title-when-truncated.js';
import { cn } from '@/lib/utils.js';

import {
  ArtifactRecipientPicker,
  type ShareRecipient,
} from './ArtifactRecipientPicker.js';
import { ArtifactSharesDisclosure } from './ArtifactSharesDisclosure.js';
import { summarizeArtifactShares } from './artifact-share-summary.js';
import {
  accessReasonLabel,
  artifactSharingLevels,
  initialShareVisibility,
  type ArtifactSharingLevel,
} from './artifact-sharing.js';

/** The icons the levels and the access list draw, resolved from the pure module's names. */
const LEVEL_ICON = {
  lock: LockIcon,
  chat: MessageSquareIcon,
  users: UsersIcon,
  globe: GlobeIcon,
} as const;

/**
 * Set who can read one file.
 *
 * The dialog answers two different questions, and they are two tabs because they are two different acts.
 * Inside the workspace it hands out ACCESS — a grant, revocable, to a file that stays where it is.
 * Outside the workspace it hands out a COPY, which stops being the author's the moment somebody takes it.
 * Stacked in one column those two read as one long ladder of reach, which they are not, and the column
 * was 1600px in a 772px dialog.
 *
 * In a personal workspace only the second question exists. The four levels decide who among the other
 * members may read the file, and there are no other members — so the levels go, the tab strip goes with
 * them, and what is left is the whole dialog. A lone tab beside an absent one reads like a control
 * somebody forgot to finish, which is the same call the conversation dialog makes for the same reason.
 * Handing a copy out is not a workspace act: a file somebody received into their own space can be passed
 * on, and a file made there can be sent to a colleague, exactly as the service already allows.
 *
 * The body mounts only while the dialog is open, so each open reads the file's current audience from the
 * server and seeds the form from it once — no sync effect, no stale carry-over between opens. That gate
 * is what stops an untouched Save from replacing a list somebody else just changed.
 */
export function ArtifactShareDialog({
  workspaceId,
  artifact,
  sharing,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  /**
   * Whether the workspace has other members, read from its registry row by the surface that opened this
   * dialog — the same value that decides the promotion confirmation's wording, so the row and the dialog
   * cannot disagree about the workspace they are both describing.
   */
  sharing: WorkspaceSharing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <ArtifactShareDialogContent
          workspaceId={workspaceId}
          artifact={artifact}
          initialOffersVisibility={offersSharingControls(sharing)}
          onOpenChange={onOpenChange}
        />
      )}
    </Dialog>
  );
}

/**
 * The form offered when the dialog opens stays fixed until it closes. The workspace row can resolve
 * from unknown to shared while somebody is choosing recipients; changing forms then would unmount the
 * outside draft and discard their input. Closing unmounts this component, so the next open still reads
 * the latest workspace state.
 */
function ArtifactShareDialogContent({
  workspaceId,
  artifact,
  initialOffersVisibility,
  onOpenChange,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  initialOffersVisibility: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [offersVisibility] = useState(initialOffersVisibility);

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Share file</DialogTitle>
        <DialogDescription>
          {offersVisibility
            ? `Choose who can open ${artifact.name}.`
            : `Send a copy of ${artifact.name} to someone outside this workspace.`}
        </DialogDescription>
      </DialogHeader>

      {offersVisibility ? (
        <ShareBody
          workspaceId={workspaceId}
          artifact={artifact}
          onOpenChange={onOpenChange}
        />
      ) : (
        <OutsideOnlyShare workspaceId={workspaceId} artifactId={artifact.id} />
      )}
    </DialogContent>
  );
}

/**
 * The outside half standing alone, for a workspace that holds nobody but the caller.
 *
 * It skips the detail read the visibility form needs, for the reason the conversation dialog wrote down:
 * handing a copy out asks the server nothing first, so a slow or failed audience read must not be able to
 * take that away. The draft lives here rather than inside the panel because the panel is the same one the
 * tabs mount, and there is no tab here to unmount it.
 */
function OutsideOnlyShare({
  workspaceId,
  artifactId,
}: {
  workspaceId: string;
  artifactId: string;
}): JSX.Element {
  const [draft, setDraft] = useState<OutsideDraft>({
    accessMode: 'named',
    recipients: [],
  });

  return (
    <ShareBeyondWorkspace
      workspaceId={workspaceId}
      artifactId={artifactId}
      draft={draft}
      onDraftChange={setDraft}
    />
  );
}

/**
 * Loads the file's stored audience, then hands it to the form. Save exists only once the server's list is
 * in hand: seeding the picker from an empty set would make an untouched Save wipe the group, which is the
 * bug the conversation dialog already learned the hard way.
 */
function ShareBody({
  workspaceId,
  artifact,
  onOpenChange,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const detailQuery = useQuery({
    queryKey: ['artifact-share', workspaceId, artifact.id],
    queryFn: () => fetchWorkspaceArtifact(workspaceId, artifact.id),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  if (detailQuery.isError) {
    return (
      <>
        <p className="text-sm text-destructive">
          Could not load who this file is shared with. Close the dialog and try
          again.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </>
    );
  }

  const detail = detailQuery.data;
  if (!detail) {
    return (
      <div className="flex flex-col gap-2" aria-busy>
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-9 w-full rounded-md" />
        ))}
      </div>
    );
  }

  return (
    <ShareForm
      workspaceId={workspaceId}
      artifact={detail}
      sharedWith={detail.sharedWith ?? []}
      onOpenChange={onOpenChange}
    />
  );
}

/** The half-written share the second tab holds, kept out here so switching tabs cannot lose it. */
interface OutsideDraft {
  accessMode: ArtifactShareAccessMode;
  recipients: ShareRecipient[];
}

/** True when two id sets hold the same ids — what tells an edited group apart from an untouched one. */
function sameMembers(
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): boolean {
  return left.size === right.size && [...left].every((id) => right.has(id));
}

/**
 * The form itself, seeded from the loaded audience. Somebody the caller cannot see in the directory (one
 * who left the workspace, say) stays in the selected set and is sent back untouched, so saving never
 * drops a person the picker never showed.
 *
 * Every piece of unsaved work lives HERE rather than inside a tab, because Radix unmounts the tab a user
 * leaves. Two ways to lose somebody's work follow from the split, and both are closed here: the footer
 * sits outside the tabs so `Save` is one click away from either of them, and the half-written outside
 * share is held as a draft so coming back to it finds the recipients still picked.
 *
 * The footer exists only while there IS something to save, which is the same predicate that draws the
 * unsaved line. A permanent full-strength `Save` was worse than useless on the outside tab: it sat
 * directly under `Share a copy`, so two buttons looked like the primary action while doing unrelated
 * things, and the one that read as "save my share" applied a visibility change that in most cases was not
 * pending at all. Nothing pending means nothing to advertise — the dialog's own corner close and Escape
 * dismiss it, and `Cancel` only earns its place when there is a change to abandon. The reachability
 * property is untouched: the moment a level is changed, on either tab, the footer is back with it.
 */
function ShareForm({
  workspaceId,
  artifact,
  sharedWith,
  onOpenChange,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  sharedWith: readonly string[];
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const levels = useMemo(() => artifactSharingLevels(artifact), [artifact]);
  const savedVisibility = useMemo(
    () => initialShareVisibility(artifact),
    [artifact],
  );
  const savedIds = useMemo(() => new Set(sharedWith), [sharedWith]);
  const [visibility, setVisibility] =
    useState<ArtifactVisibility>(savedVisibility);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(savedIds);
  const [outsideDraft, setOutsideDraft] = useState<OutsideDraft>({
    accessMode: 'named',
    recipients: [],
  });
  const share = useSetArtifactVisibility(workspaceId);
  const sharesQuery = useQuery(
    artifactSharesQueryOptions(workspaceId, artifact.id),
  );
  const liveShareCount = summarizeArtifactShares(
    sharesQuery.data ?? [],
  ).liveCount;

  const toggleMember = useMemo(
    () => (userId: string, selected: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selected) next.add(userId);
        else next.delete(userId);
        return next;
      });
    },
    [],
  );

  function save(): void {
    share.mutate(
      {
        id: artifact.id,
        visibility,
        userIds: visibility === 'group' ? [...selectedIds] : undefined,
      },
      {
        onSuccess: () => {
          notify.success('Sharing updated');
          onOpenChange(false);
        },
        onError: (error) =>
          notify.error(
            error instanceof Error ? error.message : 'Failed to update sharing',
          ),
      },
    );
  }

  /** A group of nobody is refused by the service, so the button says so before the round trip. */
  const canSave =
    !share.isPending && !(visibility === 'group' && selectedIds.size === 0);

  /**
   * Whether `Save` still has anything to apply. It is said out loud because the second tab hides the
   * radios that were changed, and a change a person cannot see is a change they will walk away from.
   */
  const unsaved =
    visibility !== savedVisibility ||
    (visibility === 'group' && !sameMembers(selectedIds, savedIds));

  /**
   * The "Unsaved change" note only draws alongside an enabled `Save`. A picked `Group` with nobody
   * in it yet is not something the dialog can save, so the footer keeps Cancel/Save but withholds
   * the note until there is a change `Save` could actually apply.
   */
  const showUnsavedNote = unsaved && canSave;

  return (
    <>
      <Tabs defaultValue="inside">
        <TabsList>
          <TabsTrigger value="inside">In this workspace</TabsTrigger>
          <TabsTrigger value="outside">
            {outsideTabLabel(liveShareCount)}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inside" className="flex flex-col gap-2">
          <div
            className="flex flex-col gap-2"
            role="radiogroup"
            aria-label="File visibility"
          >
            {levels.map((level) => (
              <LevelOption
                key={level.visibility}
                level={level}
                selected={visibility === level.visibility}
                onSelect={() => setVisibility(level.visibility)}
              />
            ))}
          </div>

          {visibility === 'group' && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">People</span>
              <WorkspaceMemberPicker
                workspaceId={workspaceId}
                selectedIds={selectedIds}
                onToggle={toggleMember}
              />
            </div>
          )}

          <AccessList workspaceId={workspaceId} artifactId={artifact.id} />
        </TabsContent>

        <TabsContent value="outside">
          <ShareBeyondWorkspace
            workspaceId={workspaceId}
            artifactId={artifact.id}
            draft={outsideDraft}
            onDraftChange={setOutsideDraft}
          />
        </TabsContent>
      </Tabs>

      {unsaved && (
        <DialogFooter className="sm:items-center">
          {showUnsavedNote ? (
            <span
              data-testid="artifact-share-unsaved"
              className="text-xs text-warning-600 sm:mr-auto"
            >
              Unsaved change to who can open this file
            </span>
          ) : (
            <span className="sm:mr-auto" />
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={share.isPending}
          >
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave}>
            {share.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}

/** One selectable level, drawn like the conversation dialog's rows so the two read as one control. */
function LevelOption({
  level,
  selected,
  onSelect,
}: {
  level: ArtifactSharingLevel;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const Icon = LEVEL_ICON[level.icon];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:bg-accent',
      )}
    >
      <Icon
        className="size-4 shrink-0 text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="flex-1">
        <span className="block text-sm font-medium">{level.label}</span>
        <span className="block text-xs text-muted-foreground">
          {level.hint}
        </span>
      </span>
    </button>
  );
}

/**
 * Who can read the file right now, as the server resolves it — not as the form currently reads. It
 * answers "did that actually share it with the people I meant", which the radio alone cannot: the chat
 * audience and the workspace directory are both facts this screen does not hold.
 *
 * A disclosure, closed, with the count in the summary. The count is the part people actually read — "is
 * it three or is it thirty" — and the names are what they open it for; leaving a scrolling list of them
 * open cost more of this dialog than the answer was worth.
 *
 * Deliberately not re-fetched while the form is edited. It describes what is SAVED, and a list that
 * jumped ahead of the Save button would be a promise the server has not made yet.
 */
function AccessList({
  workspaceId,
  artifactId,
}: {
  workspaceId: string;
  artifactId: string;
}): JSX.Element {
  const accessQuery = useQuery({
    queryKey: ['artifact-access', workspaceId, artifactId],
    queryFn: () => fetchArtifactAccess(workspaceId, artifactId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const entries = accessQuery.data;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-t pt-3"
      data-testid="artifact-access-disclosure"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 text-left">
        <ChevronRightIcon
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="text-sm font-medium">
          {entries === undefined
            ? 'Who has access now'
            : `Who has access now (${entries.length})`}
        </span>
      </CollapsibleTrigger>

      <CollapsibleContent className="pt-1.5">
        {accessQuery.isLoading && (
          <div className="flex flex-col gap-1.5" aria-busy>
            {[0, 1].map((row) => (
              <Skeleton key={row} className="h-7 w-full rounded-md" />
            ))}
          </div>
        )}
        {accessQuery.isError && (
          <p className="text-sm text-destructive">
            Couldn&apos;t load who has access.
          </p>
        )}
        {entries !== undefined && (
          <ul className="flex max-h-40 flex-col gap-0.5 overflow-y-auto">
            {entries.map((entry) => (
              <li
                key={entry.userId}
                className="flex items-center gap-2.5 rounded-md px-2 py-1 text-sm"
              >
                <span
                  className="min-w-0 flex-1 truncate"
                  onMouseEnter={titleWhenTruncated}
                >
                  {entry.name ?? entry.email ?? entry.userId}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {accessReasonLabel(entry.reason)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Hand the file to somebody the workspace does not contain.
 *
 * This is a different act from the four levels on the other tab, and the tab it lives on is what says so.
 * Those levels decide who inside the workspace may read the file; this one sends a COPY out, and once the
 * recipient has taken it the copy is theirs — the author cannot see it, change it or take it back. That
 * is why the consequence is a permanent line above the button and not a tooltip: it is the one thing
 * about this control a person can get wrong only once.
 *
 * How wide the link opens is a choice of three, and only the narrowest one addresses anybody: `internal`
 * and `public` carry no recipients at all, so the people picker belongs to `named` and disappears with
 * it. `public` is the one option in the whole product that puts a file in front of somebody who has not
 * signed in, so it says that in its own permanent line rather than leaving it to be inferred from a
 * label.
 *
 * The half-written share is the caller's state, not this component's, because this component unmounts
 * whenever somebody looks at the other tab.
 */
function ShareBeyondWorkspace({
  workspaceId,
  artifactId,
  draft,
  onDraftChange,
}: {
  workspaceId: string;
  artifactId: string;
  draft: OutsideDraft;
  onDraftChange: (draft: OutsideDraft) => void;
}): JSX.Element {
  const create = useCreateArtifactShare();
  const addressed = draft.accessMode === 'named';

  function share(): void {
    if (addressed && draft.recipients.length === 0) return;
    create.mutate(
      {
        workspaceId,
        artifactId,
        accessMode: draft.accessMode,
        subjects: addressed
          ? draft.recipients.map((recipient) => ({
              subjectType: recipient.subjectType,
              subjectId: recipient.subjectId,
            }))
          : [],
      },
      {
        onSuccess: (created) => {
          onDraftChange({ accessMode: draft.accessMode, recipients: [] });
          const url = safeHttpUrl(artifactShareUrl(created.id));
          if (url !== '') {
            void copyToClipboard(url, 'Share link copied — send it to them');
          }
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex flex-col gap-2"
        role="radiogroup"
        aria-label="Who can open the link"
      >
        {BEYOND_MODES.map((option) => (
          <ModeOption
            key={option.mode}
            option={option}
            selected={draft.accessMode === option.mode}
            onSelect={() =>
              onDraftChange({ ...draft, accessMode: option.mode })
            }
          />
        ))}
      </div>

      {addressed && (
        <>
          <ArtifactRecipientPicker
            recipients={draft.recipients}
            onPick={(recipient) =>
              onDraftChange({
                ...draft,
                recipients: [...draft.recipients, recipient],
              })
            }
          />

          <div
            data-testid="artifact-share-recipients"
            className="flex flex-wrap gap-1.5"
          >
            {draft.recipients.map((recipient) => (
              <span
                key={`${recipient.subjectType}:${recipient.subjectId}`}
                className="flex max-w-full items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 pl-2.5 pr-1 text-xs"
              >
                {recipient.subjectType === 'entra_group' && (
                  <UsersIcon
                    className="size-3 shrink-0 text-muted-foreground"
                    strokeWidth={2}
                  />
                )}
                <span
                  className="min-w-0 truncate"
                  onMouseEnter={titleWhenTruncated}
                >
                  {recipient.label}
                  {recipient.subjectType === 'entra_group' && (
                    <span className="text-muted-foreground">
                      {' · '}
                      {groupMemberCountLabel(recipient.memberCount)}
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${recipient.label}`}
                  onClick={() =>
                    onDraftChange({
                      ...draft,
                      recipients: draft.recipients.filter(
                        (kept) =>
                          kept.subjectType !== recipient.subjectType ||
                          kept.subjectId !== recipient.subjectId,
                      ),
                    })
                  }
                >
                  <XIcon className="size-3" strokeWidth={2} />
                </Button>
              </span>
            ))}
          </div>
        </>
      )}

      <p
        data-testid="artifact-share-copy-warning"
        className="border-l-2 border-border bg-muted/40 py-2 pl-3 pr-2.5 text-xs leading-relaxed text-foreground"
      >
        <span className="font-medium">
          They get their own copy of this file.
        </span>{' '}
        Once someone has taken their copy you cannot take it back — revoking a
        share only stops the people who have not taken one yet.
      </p>

      {draft.accessMode === 'public' && (
        <p
          data-testid="artifact-share-public-warning"
          className="text-xs font-medium text-destructive"
        >
          Anyone with this link will be able to read this file without signing
          in to the platform.
        </p>
      )}

      <Button
        size="sm"
        className="self-start"
        disabled={
          (addressed && draft.recipients.length === 0) || create.isPending
        }
        onClick={share}
      >
        {create.isPending ? 'Sharing…' : 'Share a copy'}
      </Button>

      <ArtifactSharesDisclosure
        workspaceId={workspaceId}
        artifactId={artifactId}
      />
    </div>
  );
}

/**
 * The three widths a link opens to, in the order the product means them: the people and groups you name,
 * everybody at Customertimes, then anybody at all. The labels are the ones a created share is listed under, so the
 * choice and the record of it read as the same words.
 */
const BEYOND_MODES = [
  {
    mode: 'named' as const,
    label: 'Specific people and groups',
    hint: 'Only the people and groups you pick can open the link.',
    icon: UsersIcon,
  },
  {
    mode: 'internal' as const,
    label: 'Customertimes employees',
    hint: 'Anyone at Customertimes who has the link and is signed in.',
    icon: Building2Icon,
  },
  {
    mode: 'public' as const,
    label: 'Anyone with the link',
    hint: 'No sign-in needed.',
    icon: GlobeIcon,
  },
];

/** One width of link, drawn like the visibility rows so the whole dialog reads as one control. */
function ModeOption({
  option,
  selected,
  onSelect,
}: {
  option: (typeof BEYOND_MODES)[number];
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const Icon = option.icon;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:bg-accent',
      )}
    >
      <Icon
        className="size-4 shrink-0 text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="flex-1">
        <span className="block text-sm font-medium">{option.label}</span>
        <span className="block text-xs text-muted-foreground">
          {option.hint}
        </span>
      </span>
    </button>
  );
}
